/**
 * EntitySubtypeIcon — unified renderer for any CustomerType subtype.
 *
 * Strategy:
 *   1. Try pcb-modal (PCB-bespoke v2.3/v2.5 React component) first — primary path
 *   2. Fall back to flat-library SP-gap icons (MedicalSetAside, USDAGrant) — closes the v2.5 gap
 *   3. Render nothing if neither resolves (caller's responsibility to provide a label)
 *
 * Bill 2026-05-13 — closes the visual gap for MedicalSetAside + USDAGrant
 * subtypes which don't have v2.5 PCB-bespoke icons yet. Once v2.6 ships
 * with those two, this fallback path becomes a no-op.
 */
import type { JSX, ComponentType } from 'react';
import * as PcbModal from './pcb-modal';
import { specialPurposeGapIconUrl, type SpecialPurposeGapSlug } from './flat-library';
import type { IconProps } from './pcb-modal/types';

/**
 * Map every CustomerType (the string enum value used across EntityRules,
 * Cart.PeopleJson, and shared-types) to a pcb-modal React component.
 *
 * Personal cohort: Individual / Minor / NonResidentAlien all use the
 * PersonalIcon bucket. UTMA gets its own subtype icon.
 */
const PCB_MODAL_BY_CUSTOMER_TYPE: Readonly<Record<string, ComponentType<IconProps>>> = {
  // Personal cohort
  Individual:       PcbModal.PersonalIcon,
  Minor:            PcbModal.PersonalIcon,
  NonResidentAlien: PcbModal.PersonalIcon,
  UTMA:             PcbModal.UTMAIcon,

  // Business cohort
  Corporation:      PcbModal.CorporationIcon,
  LLC:              PcbModal.LLCIcon,
  Partnership:      PcbModal.PartnershipIcon,
  // Sole Proprietor: the entity catalog (entityGroups.ts) keys this subtype as
  // `SoleProp`; the legacy EntityRules/config rows key it as `DBA`. Map BOTH to
  // the same icon so the Business subtype card renders an icon regardless of
  // which key reaches this renderer (the `DBA`-only map silently dropped the
  // `SoleProp` card's icon — Bill 2026-06-13).
  SoleProp:         PcbModal.SoleProprietorshipIcon,
  DBA:              PcbModal.SoleProprietorshipIcon,

  // Trust & Estate
  RevocableTrust:   PcbModal.RevocableTrustIcon,
  IrrevocableTrust: PcbModal.IrrevocableTrustIcon,
  Estate:           PcbModal.EstateIcon,

  // Special Purpose (v2.5 set — 3 of 5; MedicalSetAside + USDAGrant fall through)
  SelfSettledSNT:   PcbModal.SelfSettledSNTIcon,
  QIT:              PcbModal.QITIcon,
  IOLTA:            PcbModal.IOLTAIcon,

  // Fiduciary-Managed
  Conservator:      PcbModal.ConservatorIcon,
  RepPayee:         PcbModal.RepPayeeIcon,
  VAFiduciary:      PcbModal.VAFiduciaryIcon,

  // Charitable
  NonProfit:        PcbModal.NonProfitIcon,
  UnincorpAssoc:    PcbModal.UnincorporatedAssociationIcon,
  Donation:         PcbModal.DonationIcon,

  // Government / Political
  Political:        PcbModal.PoliticalCommitteeIcon,
  PublicFund:       PcbModal.PublicFundsIcon,
};

/**
 * CustomerType values that have no pcb-modal component yet — fall back to
 * a flat-library SVG. When v2.6 ships PCB-bespoke versions, move them up
 * into PCB_MODAL_BY_CUSTOMER_TYPE and delete the entry here.
 */
const FLAT_FALLBACK_BY_CUSTOMER_TYPE: Readonly<Record<string, SpecialPurposeGapSlug>> = {
  MedicalSetAside: 'medical-set-aside',
  USDAGrant:       'usda-grant',
};

export interface EntitySubtypeIconProps {
  /** CustomerType enum value (Individual / Corporation / etc.). */
  customerType: string;
  /** Pixel size. Default 40. */
  size?: number;
  /** Override accessible label. Default derives from customerType. */
  'aria-label'?: string;
}

/**
 * Renders the appropriate icon for a given CustomerType subtype.
 * Returns null when no mapping exists (caller should rely on text label).
 */
export function EntitySubtypeIcon({
  customerType,
  size = 40,
  'aria-label': ariaLabel,
}: EntitySubtypeIconProps): JSX.Element | null {
  const Component = PCB_MODAL_BY_CUSTOMER_TYPE[customerType];
  if (Component) {
    return <Component size={size} aria-label={ariaLabel ?? customerType} />;
  }
  const fallbackSlug = FLAT_FALLBACK_BY_CUSTOMER_TYPE[customerType];
  if (fallbackSlug) {
    const url = specialPurposeGapIconUrl(fallbackSlug);
    if (url) {
      return (
        <img
          src={url}
          alt={ariaLabel ?? customerType}
          width={size}
          height={size}
          style={{ display: 'block' }}
        />
      );
    }
  }
  return null;
}

export default EntitySubtypeIcon;
